import { apiGet, apiPost, apiPut, apiDelete, apiUpload } from './client';

export interface EventType {
  id: number;
  name: string;
  maxParticipants: number | null;
  rsvpDeadlineDays: number | null;
  eventCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface EventTypeDropdown {
  id: number;
  name: string;
  maxParticipants: number | null;
  rsvpDeadlineDays: number | null;
}

export interface TeamEventListItem {
  id: number;
  seriesId: number | null;
  eventType: { id: number; name: string; maxParticipants: number | null };
  title: string | null;
  description: string | null;
  location: string | null;
  videoCallUrl: string | null;
  status: 'draft' | 'published' | 'series_template' | 'cancelled';
  eventDate: string;
  eventTime: string | null;
  endDate: string | null;
  endTime: string | null;
  rsvpDeadlineDays: number | null;
  isInviteOnly: boolean;
  /** MKS-533 — the event is open to external guests. */
  allowExternalGuests: boolean;
  /** MKS-540 — guests one participant may bring; null while the event is not open to guests. */
  maxGuestsPerPerson: number | null;
  createdBy: { id: number; firstName: string | null; lastName: string | null };
  organizers: { id: number; firstName: string | null; lastName: string | null }[];
  participantCount: number;
  /** MKS-533 — guests across the going list; 0 unless the event allows them. */
  guestCount: number;
  /** MKS-533 — guests the signed-in user brings. */
  myGuestCount: number;
  isParticipating: boolean;
  isDeclined: boolean;
  createdAt: string | null;
}

export interface TeamEventDetail extends TeamEventListItem {
  participants: { id: number; firstName: string | null; lastName: string | null; noShow: boolean; guestCount: number }[];
  declinedUsers: { id: number; firstName: string | null; lastName: string | null }[];
  seriesSiblings?: { id: number; eventDate: string; status: 'draft' | 'published' | 'series_template' | 'cancelled' }[];
  isDetached: boolean;
  recurrenceEndDate: string | null;
}

export interface CreateTeamEventInput {
  eventTypeId: number;
  title?: string | null;
  description?: string | null;
  location?: string | null;
  videoCallUrl?: string | null;
  eventDate: string;
  eventTime?: string | null;
  endDate?: string | null;
  endTime?: string | null;
  organizerIds?: number[];
  rsvpDeadlineDays?: number | null;
  isInviteOnly?: boolean;
  allowExternalGuests?: boolean;
  /** MKS-540 — 1..MAX_EVENT_GUESTS; omitted while the event is not open to guests. */
  maxGuestsPerPerson?: number | null;
  recurrencePattern?: 'weekly' | 'biweekly' | 'monthly';
}

// Event Types (Admin)
export async function getEventTypes(): Promise<EventType[]> {
  const res = await apiGet<{ data: EventType[] }>('/admin/event-types');
  return res.data;
}

export async function createEventType(name: string, maxParticipants?: number | null, rsvpDeadlineDays?: number | null): Promise<EventType> {
  const res = await apiPost<{ data: EventType }>('/admin/event-types', { name, maxParticipants: maxParticipants ?? null, rsvpDeadlineDays: rsvpDeadlineDays ?? null });
  return res.data;
}

export async function updateEventType(id: number, name: string, maxParticipants?: number | null, rsvpDeadlineDays?: number | null): Promise<EventType> {
  const res = await apiPut<{ data: EventType }>(`/admin/event-types/${id}`, { name, maxParticipants: maxParticipants ?? null, rsvpDeadlineDays: rsvpDeadlineDays ?? null });
  return res.data;
}

export async function deleteEventType(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/admin/event-types/${id}`);
}

// Event Types Dropdown (all users)
export async function getEventTypesDropdown(): Promise<EventTypeDropdown[]> {
  const res = await apiGet<{ data: EventTypeDropdown[] }>('/team-events/event-types');
  return res.data;
}

export interface UpdateTeamEventInput {
  eventTypeId?: number;
  title?: string | null;
  description?: string | null;
  location?: string | null;
  videoCallUrl?: string | null;
  eventDate?: string;
  eventTime?: string | null;
  endDate?: string | null;
  endTime?: string | null;
  organizerIds?: number[];
  rsvpDeadlineDays?: number | null;
  isInviteOnly?: boolean;
  allowExternalGuests?: boolean;
  /** MKS-540 — 1..MAX_EVENT_GUESTS; omitted leaves the stored limit untouched. */
  maxGuestsPerPerson?: number | null;
}

export type TeamEventScope = 'this' | 'this_and_following' | 'entire_series';

// Team Events
export async function getTeamEvents(): Promise<{ data: TeamEventListItem[]; total: number }> {
  return apiGet<{ data: TeamEventListItem[]; total: number }>('/team-events');
}

export async function getTeamEvent(id: number): Promise<TeamEventDetail> {
  const res = await apiGet<{ data: TeamEventDetail }>(`/team-events/${id}`);
  return res.data;
}

export async function createTeamEvent(input: CreateTeamEventInput): Promise<TeamEventDetail> {
  const res = await apiPost<{ data: TeamEventDetail }>('/team-events', input);
  return res.data;
}

export async function updateTeamEvent(id: number, input: UpdateTeamEventInput): Promise<TeamEventDetail> {
  const res = await apiPut<{ data: TeamEventDetail }>(`/team-events/${id}`, input);
  return res.data;
}

export async function deleteTeamEvent(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/team-events/${id}`);
}

export async function publishTeamEvent(id: number): Promise<TeamEventDetail> {
  const res = await apiPost<{ data: TeamEventDetail }>(`/team-events/${id}/publish`);
  return res.data;
}

export async function updateTeamEventScoped(
  id: number,
  input: UpdateTeamEventInput,
  scope: TeamEventScope,
): Promise<TeamEventDetail> {
  const res = await apiPut<{ data: TeamEventDetail }>(`/team-events/${id}`, { ...input, scope });
  return res.data;
}

export async function cancelInstance(id: number): Promise<TeamEventDetail> {
  const res = await apiPost<{ data: TeamEventDetail }>(`/team-events/${id}/cancel`);
  return res.data;
}

export async function cancelSeries(seriesId: number): Promise<{ cancelledCount: number }> {
  const res = await apiPost<{ data: { cancelledCount: number } }>(`/team-events/${seriesId}/cancel-series`);
  return res.data;
}

export async function endSeries(seriesId: number, endDate: string): Promise<{ deletedCount: number }> {
  const res = await apiPost<{ data: { deletedCount: number } }>(`/team-events/${seriesId}/end-series`, {
    endDate,
  });
  return res.data;
}

// Participation
export async function participate(eventId: number): Promise<number> {
  const res = await apiPost<{ data: { success: boolean; participantCount: number } }>(`/team-events/${eventId}/participate`);
  return res.data.participantCount;
}

export async function unparticipate(eventId: number): Promise<number> {
  const res = await apiDelete<{ data: { success: boolean; participantCount: number } }>(`/team-events/${eventId}/participate`);
  return res.data.participantCount;
}

export async function declineEvent(eventId: number): Promise<number> {
  const res = await apiPost<{ data: { success: boolean; participantCount: number } }>(`/team-events/${eventId}/decline`);
  return res.data.participantCount;
}

// MKS-533 — upper bound on the guests one participant may bring; mirrors
// MAX_EVENT_GUESTS in backend/src/services/teamEventService.ts.
export const MAX_EVENT_GUESTS = 20;

export interface GuestCountResult {
  guestCount: number;
  participantCount: number;
  totalHeadcount: number;
}

export async function setGuestCount(eventId: number, guestCount: number): Promise<GuestCountResult> {
  const res = await apiPut<{ data: { success: boolean } & GuestCountResult }>(
    `/team-events/${eventId}/guests`,
    { guestCount },
  );
  return res.data;
}

export async function addParticipant(eventId: number, userId: number, status: 'going' | 'not_going' = 'going'): Promise<number> {
  const res = await apiPost<{ data: { success: boolean; participantCount: number } }>(`/team-events/${eventId}/participants`, { userId, status });
  return res.data.participantCount;
}

export async function adminRemoveParticipant(eventId: number, userId: number): Promise<number> {
  const res = await apiDelete<{ data: { success: boolean; participantCount: number } }>(`/team-events/${eventId}/participants/${userId}`);
  return res.data.participantCount;
}

export async function markNoShow(eventId: number, userId: number, noShow: boolean): Promise<void> {
  await apiPut<{ data: { success: boolean } }>(`/team-events/${eventId}/participants/${userId}/no-show`, { noShow });
}

// Organizer candidates (Wheel of Fortune)
export interface OrganizerCandidate {
  id: number;
  firstName: string | null;
  lastName: string | null;
}

export async function getTeamEventPendingCount(): Promise<number> {
  const res = await apiGet<{ data: { count: number } }>('/team-events/pending-count');
  return res.data.count;
}

export async function getOrganizerCandidates(eventTypeId: number, eventDate?: string, eventId?: number): Promise<OrganizerCandidate[]> {
  let url = `/team-events/organizer-candidates?eventTypeId=${eventTypeId}`;
  if (eventDate) url += `&eventDate=${eventDate}`;
  if (eventId) url += `&eventId=${eventId}`;
  const res = await apiGet<{ data: OrganizerCandidate[] }>(url);
  return res.data;
}

// Photos
export interface TeamEventPhoto {
  id: number;
  eventId: number;
  fileName: string;
  contentType: string;
  fileSize: number;
  url: string;
  uploadedBy: { id: number; firstName: string | null; lastName: string | null };
  createdAt: string;
}

export interface GalleryEvent {
  eventId: number;
  eventName: string;
  eventDate: string;
  eventTypeName: string;
  photos: TeamEventPhoto[];
}

export async function uploadEventPhoto(eventId: number, file: File): Promise<TeamEventPhoto> {
  const res = await apiUpload<{ data: TeamEventPhoto }>(`/team-events/${eventId}/photos`, file);
  return res.data;
}

export async function getEventPhotos(eventId: number): Promise<TeamEventPhoto[]> {
  const res = await apiGet<{ data: TeamEventPhoto[] }>(`/team-events/${eventId}/photos`);
  return res.data;
}

export async function deleteEventPhoto(eventId: number, photoId: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/team-events/${eventId}/photos/${photoId}`);
}

export async function getEventGallery(): Promise<GalleryEvent[]> {
  const res = await apiGet<{ data: GalleryEvent[] }>('/team-events/gallery');
  return res.data;
}
